import { EscalatedToDisplayTextMap, RequestActionType } from '@strata/react-chat';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { handleError } from 'services/Error';
import { type PayerRequestActionDialogProps } from 'services/interfaces';
import { usePostRequestActivityMutation } from 'store/api/payerRequestApi';

const Title = 'Update Internal Escalation Status';

const ValidationSchema = Yup.object({
    escalatedTo: Yup.string().required(validationStrings.requiredField.errorMessage),
});

const RESOLVE_ESCALATION = 'Resolve Internal Escalation';
const StatusOptions = [
    ...Object.values(EscalatedToDisplayTextMap).map((value) => `Escalate to ${value}`),
    RESOLVE_ESCALATION,
];

export const UpdateInternalEscalationStatusDialog = ({
    onClose,
    payerId,
    requestDetails,
}: PayerRequestActionDialogProps) => {
    const [postRequestActivity] = usePostRequestActivityMutation();
    return (
        <Formik
            initialValues={{} as Yup.InferType<typeof ValidationSchema>}
            enableReinitialize
            validateOnMount
            validationSchema={ValidationSchema}
            onSubmit={async (values, { resetForm }) => {
                try {
                    const escalatedToWithoutPrefix = values.escalatedTo.replace('Escalate to ', '');
                    const actionDetail =
                        values.escalatedTo === RESOLVE_ESCALATION
                            ? undefined
                            : Object.entries(EscalatedToDisplayTextMap).find(
                                  ([, text]) => text === escalatedToWithoutPrefix,
                              )?.[0];
                    await postRequestActivity({
                        payerId,
                        requestId: requestDetails.requestId,
                        actionType: RequestActionType.ESCALATED,
                        actionDetail,
                    }).unwrap();

                    let message;
                    if (values.escalatedTo === RESOLVE_ESCALATION) {
                        message = 'Status successfully updated to In Progress.';
                    } else {
                        message = `Status successfully updated to Escalated to ${escalatedToWithoutPrefix}.`;
                    }

                    SuccessToast({ message });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError(
                        'Unable to update internal escalation status. Please try again.',
                        err as Error,
                    );
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={600}
                    isOpen
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update Status"
                    isPrimaryButtonDisabled={
                        !(formik.isValid && formik.dirty) || formik.isSubmitting
                    }
                    dialogContent={
                        <SuggestSingleSelectFormik
                            id="escalatedTo"
                            label="Status"
                            values={StatusOptions}
                            required
                        />
                    }
                />
            )}
        </Formik>
    );
};

UpdateInternalEscalationStatusDialog.Title = Title;
